import { z } from 'zod';
import { v1PaginationQuerySchema, v1PaginationSchema } from './pagination';
import { csvParam, v1SortOrderSchema } from './query';
import { v1PlatformSchema } from './vocabulary';
/**
 * Public hook-inventory contract for `/api/v1` (KIR-4431, slice 1).
 *
 * Field names follow `apps/backend/src/routes/v1/NAMING.md`.
 *
 * **Gated twice.** The role gate is manager, owner or support, copied from the
 * UI route — a hook is code that runs automatically on a developer's machine,
 * attributed to named people. On top of that the whole resource sits behind the
 * `hook_inventory_enabled` feature flag; while it is off every endpoint here
 * answers `409 Conflict`, per the decision recorded on KIR-4431.
 *
 * **The hook bodies are not published.** The internal API exposes a `/content`
 * sub-resource returning the raw script; `/api/v1` deliberately does not. Hook
 * scripts are user-authored and can contain anything, so publishing them is a
 * decision to take explicitly rather than by proximity — the same call made for
 * AI instructions.
 */
/** The kind of tooling a hook belongs to. */
export const v1HookTypeSchema = z.enum(['coding-agent', 'git']).meta({
    id: 'V1HookType',
    title: 'Hook type',
    description: 'What kind of tooling runs a hook: `coding-agent` for Claude / Cursor / Copilot settings hooks, `git` for `.git/hooks`, Husky and pre-commit entries.',
});
/**
 * Where the reporter found the hook.
 *
 * Hyphenated, not the underscored internal slugs (`git_dir`,
 * `pre_commit_config`). `NAMING.md` forbids publishing an internal slug, and the
 * mapping is done in the DTO — the same treatment MCP gives `running`/`idle`,
 * which it publishes as `active`/`inactive`.
 */
export const v1HookSourceTypeSchema = z
    .enum(['coding-agent', 'git-dir', 'husky', 'pre-commit-config'])
    .meta({
    id: 'V1HookSourceType',
    title: 'Hook source type',
    description: 'Where a hook was found: `coding-agent` for a coding-agent settings file, `git-dir` for `.git/hooks/`, `husky` for a Husky directory, `pre-commit-config` for `.pre-commit-config.yaml`.',
});
/**
 * One hook in the organization's inventory.
 *
 * A row is one **file content, in one type-and-event, on one platform**,
 * aggregated across every developer and project that has it. The same script
 * bound to two events is two rows, which is why `id` encodes all three
 * components and is opaque.
 */
export const v1HookSchema = z
    .object({
    id: z
        .string()
        .describe('Opaque identifier for this hook within your organization. Stable, and safe to store and pass back to the by-ID, users and projects endpoints. **Do not parse or construct it** — it encodes more than one component, because the same script bound to a different event or found on a different platform is a different row, and its format is not part of this contract.'),
    name: z
        .string()
        .nullable()
        .describe('The name Kirin generated for this hook, or null when none was generated.'),
    summary: z
        .string()
        .nullable()
        .describe('A short generated description of what the hook does, or null when none was generated.'),
    type: v1HookTypeSchema
        .nullable()
        .describe('What kind of tooling runs this hook, or null for a row whose classification could not be parsed.'),
    event: z
        .string()
        .nullable()
        .describe('The event that triggers this hook — for example `PreToolUse`, `pre-commit` or `commit-msg`. Null for a row whose classification could not be parsed.'),
    platform: v1PlatformSchema
        .nullable()
        .describe('The IDE, coding agent, or Git itself, with the display name the Kirin app shows. A git-family hook reports `{ id: "git", name: "Git" }` — matching the dedicated Git chip the Hooks screen renders for those rows — rather than the null the underlying column holds. Null only when the row could not be attributed at all.'),
    sourceType: v1HookSourceTypeSchema
        .nullable()
        .describe('Where the hook was found. Representative rather than exhaustive: a group can span more than one location — a `pre-commit` hook present both in `.git/hooks/` and under Husky — in which case this is the most common of them. Null when no reporter recorded one.'),
    sourceTypeName: z
        .string()
        .nullable()
        .describe('That location as the Kirin app labels it, e.g. "pre-commit config" for `pre-commit-config`. Null whenever `sourceType` is.'),
    filePath: z
        .string()
        .describe('The representative path for this hook — the one the Inventory screen shows.'),
    filePaths: z
        .array(z.string())
        .describe('Every distinct path this hook was found at, across your endpoint users. A superset of `filePath`.'),
    userCount: z
        .number()
        .int()
        .describe('How many of your endpoint users have this hook. The endpoint users themselves are on the `users` sub-resource.'),
    projectCount: z
        .number()
        .int()
        .describe('How many projects this hook was found in. The projects themselves are on the `projects` sub-resource.'),
    firstSeenAt: z
        .string()
        .datetime()
        .describe('When this hook was first reported, ISO 8601 UTC.'),
    lastSeenAt: z
        .string()
        .datetime()
        .describe('The most recent report of this hook, ISO 8601 UTC.'),
})
    .meta({
    id: 'V1Hook',
    title: 'Hook',
    description: "One hook in your organization's inventory — one script, in one type-and-event, on one platform — aggregated across every endpoint user and project that has it.",
    examples: [
        {
            id: '2b9e5c47-1d38-4f60-a8c2-93e7b014da5f:Z2l0OnByZS1jb21taXQ:-',
            name: 'gitleaks',
            summary: 'Runs gitleaks over staged changes before every commit.',
            type: 'git',
            event: 'pre-commit',
            platform: null,
            sourceType: 'pre-commit-config',
            sourceTypeName: 'pre-commit config',
            filePath: '.pre-commit-config.yaml',
            filePaths: ['.pre-commit-config.yaml'],
            userCount: 14,
            projectCount: 6,
            firstSeenAt: '2026-06-02T08:41:19Z',
            lastSeenAt: '2026-08-31T14:03:07Z',
        },
    ],
});
/** Sortable fields, named for the public field they sort. */
export const v1HookSortFieldSchema = z
    .enum([
    'name',
    'type',
    'platform',
    'filePath',
    'summary',
    'projectCount',
    'userCount',
    'lastSeenAt',
])
    .describe('Which field to sort by.');
/**
 * The list query.
 *
 * The filters the Inventory → Hooks screen offers, minus one: there is no
 * `event` filter. Internally an event is only addressable as part of the
 * combined `<type>:<event>` token, so filtering on it alone would mean this
 * layer composing a query of its own instead of passing one through — see the
 * boundary rules in `apps/backend/src/routes/v1/README.md`. Sort and read
 * `event` off the row instead.
 *
 * Deliberately unnamed (no `.meta({ id })`): a named schema is emitted as a
 * `$ref`, and `@fastify/swagger` cannot expand a `$ref` into a parameter list.
 */
export const v1HookListQuerySchema = v1PaginationQuerySchema.extend({
    name: csvParam('Filter by the generated hook name.'),
    type: csvParam('Filter by type — `coding-agent` or `git`.'),
    /**
     * Filter on the platform, matching `platform.id`.
     *
     * One documented edge: `platform` reports `git` for a git-family hook whose
     * stored platform is absent, because that is the Git chip the Hooks screen
     * renders for it. `platform=git` matches the stored value, so those few rows
     * — legacy or unattributed ones; ingestion has required a platform since
     * KIR-2917 — are not returned by it. The app's own filter behaves the same
     * way, so this matches the product rather than diverging from it.
     */
    platform: csvParam('Filter by the platform the hook was found on, as returned in `platform.id`. A git-family hook with no stored platform still reports `git` here — matching the Git chip the Kirin app shows — but is not matched by `platform=git`, the same as in the app.'),
    filePath: csvParam('Filter by file path.'),
    sortBy: v1HookSortFieldSchema.default('userCount'),
    sortOrder: v1SortOrderSchema.default('desc'),
});
export const v1HookListResponseSchema = z
    .object({
    data: z.array(v1HookSchema).describe('This page of hooks.'),
    pagination: v1PaginationSchema,
})
    .meta({
    id: 'V1HookList',
    title: 'Hook list',
    description: "A page of the hooks found across your organization's endpoint users.",
    examples: [
        {
            data: [
                {
                    id: '2b9e5c47-1d38-4f60-a8c2-93e7b014da5f:Z2l0OnByZS1jb21taXQ:-',
                    name: 'gitleaks',
                    summary: 'Runs gitleaks over staged changes before every commit.',
                    type: 'git',
                    event: 'pre-commit',
                    platform: null,
                    sourceType: 'pre-commit-config',
                    sourceTypeName: 'pre-commit config',
                    filePath: '.pre-commit-config.yaml',
                    filePaths: ['.pre-commit-config.yaml'],
                    userCount: 14,
                    projectCount: 6,
                    firstSeenAt: '2026-06-02T08:41:19Z',
                    lastSeenAt: '2026-08-31T14:03:07Z',
                },
            ],
            pagination: {
                page: 1,
                pageSize: 25,
                totalItems: 43,
                totalPages: 2,
                hasMore: true,
            },
        },
    ],
});
/** One developer who has a given hook. */
export const v1HookUserSchema = z
    .object({
    id: z
        .string()
        .describe('The endpoint user, as identified in the inventory-users resource. Join on this to read their full inventory record.'),
    name: z.string().nullable().describe('Display name, if known.'),
    email: z.string().nullable().describe('Email address, if known.'),
    projects: z
        .array(z.string())
        .describe('The names of the projects in which this endpoint user has the hook.'),
    lastSeenAt: z
        .string()
        .datetime()
        .describe("This endpoint user's most recent report, ISO 8601 UTC."),
})
    .meta({
    id: 'V1HookUser',
    title: 'Hook user',
    description: 'An endpoint user who has a given hook.',
    examples: [
        {
            id: '8c47a0e5-1b93-4d26-b7f8-5e0a2c96d341',
            name: 'Stella Normal',
            email: 'stella@example.com',
            projects: ['payments-api', 'billing-web'],
            lastSeenAt: '2026-08-31T14:03:07Z',
        },
    ],
});
/** The users of one hook. Not paginated. */
export const v1HookUserListResponseSchema = z
    .object({
    data: z
        .array(v1HookUserSchema)
        .describe('Every endpoint user who has this hook.'),
})
    .meta({
    id: 'V1HookUserList',
    title: 'Hook users',
    description: 'Every endpoint user in your organization who has a given hook. Not paginated.',
    examples: [
        {
            data: [
                {
                    id: '8c47a0e5-1b93-4d26-b7f8-5e0a2c96d341',
                    name: 'Stella Normal',
                    email: 'stella@example.com',
                    projects: ['payments-api', 'billing-web'],
                    lastSeenAt: '2026-08-31T14:03:07Z',
                },
            ],
        },
    ],
});
/** One project a given hook was found in. */
export const v1HookProjectSchema = z
    .object({
    id: z.string().describe("The project's identifier."),
    name: z.string().describe("The project's name."),
    slug: z.string().describe("The project's slug, as used in URLs."),
    userCount: z
        .number()
        .int()
        .describe('How many of your endpoint users have this hook in this project.'),
    lastSeenAt: z
        .string()
        .datetime()
        .describe('The most recent report of this hook in this project, ISO 8601 UTC.'),
})
    .meta({
    id: 'V1HookProject',
    title: 'Hook project',
    description: 'A project a given hook was found in.',
    examples: [
        {
            id: 'a4d1e70b-8c25-4936-9f0e-3b6a1d82c5f7',
            name: 'payments-api',
            slug: 'payments-api',
            userCount: 8,
            lastSeenAt: '2026-08-31T14:03:07Z',
        },
    ],
});
/** The projects of one hook. Not paginated. */
export const v1HookProjectListResponseSchema = z
    .object({
    data: z
        .array(v1HookProjectSchema)
        .describe('Every project this hook was found in.'),
})
    .meta({
    id: 'V1HookProjectList',
    title: 'Hook projects',
    description: 'Every project in your organization where a given hook was found. Not paginated.',
    examples: [
        {
            data: [
                {
                    id: 'a4d1e70b-8c25-4936-9f0e-3b6a1d82c5f7',
                    name: 'payments-api',
                    slug: 'payments-api',
                    userCount: 8,
                    lastSeenAt: '2026-08-31T14:03:07Z',
                },
            ],
        },
    ],
});
//# sourceMappingURL=hooks.js.map